import type { RenderStateToonOutline } from "@novorender/api";
import { useEffect, useRef } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectIsProcoreDesign } from "slices/explorer";
import { ViewMode } from "types/misc";

import { selectAdvanced, selectIsXRayActive, selectPicker, selectPoints, selectViewMode } from "../renderSlice";
import { Picker } from "../types";
import { resolveToonOutline } from "../xrayStyle";

export function useHandleAdvancedSettings() {
    const {
        state: { view, internalEventEmitter },
    } = useExplorerGlobals();
    const settings = useAppSelector(selectAdvanced);
    const pointSize = useAppSelector(selectPoints).size;
    const picker = useAppSelector(selectPicker);
    const isXRayActive = useAppSelector(selectIsXRayActive);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isSectionPlanePickerActive = picker === Picker.SectionPlane;
    const viewMode = useAppSelector(selectViewMode);
    const keepOnOutlinesDuringOrthoMove = isProcoreDesign ? false : viewMode === ViewMode.CrossSection;
    const toonOutlineRef = useRef<Partial<RenderStateToonOutline>>({});

    // Update different settings separately to keep modified render state minimal.
    // Internal checks inside View may check object references, so no need to update unrelated properties
    // more often than needed.
    useEffect(() => {
        view?.modifyRenderState({
            output: {
                samplesMSAA: settings.msaa.enabled ? settings.msaa.samples : 0,
            },
        });
    }, [view, settings.msaa]);

    useEffect(() => {
        view?.modifyRenderState({
            tonemapping: settings.tonemapping,
        });
    }, [view, settings.tonemapping]);

    useEffect(() => {
        view?.modifyRenderState({
            pick: settings.pick,
        });
    }, [view, settings.pick]);

    useEffect(() => {
        view?.modifyRenderState({
            debug: settings.debug,
        });
    }, [view, settings.debug]);

    useEffect(() => {
        view?.modifyRenderState({
            outlines: {
                breakingPointAngleThreshold: settings.outlines.breakingPointAngleThreshold,
                showVertices: !isProcoreDesign,
                keepOnDuringOrthoMove: keepOnOutlinesDuringOrthoMove,
                linearThickness: isProcoreDesign ? 0.001 : 0.01,
            },
        });
    }, [view, settings.outlines.breakingPointAngleThreshold, isProcoreDesign, keepOnOutlinesDuringOrthoMove]);

    useEffect(() => {
        if (!view) {
            return;
        }

        const toonOutline: Partial<RenderStateToonOutline> = resolveToonOutline({
            isXRayActive,
            isSectionPlanePickerActive,
            toonOutlineSettings: settings.toonOutline,
        });
        toonOutlineRef.current = toonOutline;

        view.modifyRenderState({ toonOutline });
    }, [view, settings.toonOutline, isXRayActive, isSectionPlanePickerActive]);

    // The engine turns toonOutline.on off while the camera moves, and only turns it
    // back on for idle frames when deviceProfile.tier > 0. Re-apply on cameraSettled
    // so x-ray / section-plane outlines return on every device after the camera stops.
    useEffect(() => {
        if (!view) {
            return;
        }

        return internalEventEmitter.on("cameraSettled", () => {
            const toonOutline = toonOutlineRef.current;
            if (toonOutline.on) {
                view.modifyRenderState({ toonOutline });
            }
        });
    }, [view, internalEventEmitter]);

    useEffect(() => {
        view?.modifyRenderState({
            points: {
                size: pointSize,
            },
        });
    }, [view, pointSize]);
}
